import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useContext } from 'react';
import PropTypes from 'prop-types';
import MegaMenu from './MegaMenu';
import SchoolSwitcher from './SchoolSwitcher';
import { logo } from '../imagepath';
import AuthContext from '../../context/AuthContext';
import ProfileServices from '../../services/ProfileServices';
import { getApiBaseUrl } from '../../services/httpServices';
import './MegaMenu.css';
import './TopNavbar.css';

/**
 * TopNavbar Component - Horizontal navigation bar with mega menu support
 * 
 * @param {Object} props
 * @param {string} props.role - User role ('admin', 'state', 1, 2, 3)
 * @param {Array} props.menuItems - Array of menu items with mega menu configuration
 * @param {Function} props.onLogout - Logout handler
 */
const TopNavbar = ({ role, menuItems = [], onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logoutUser } = useContext(AuthContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [userAvatar, setUserAvatar] = useState(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const profileMenuRef = useRef(null);
  const fileInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  // Get API base URL for avatar images
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    try {
      const apiBase = getApiBaseUrl().replace('/api/', '');
      return avatarPath.startsWith('http') ? avatarPath : `${apiBase}${avatarPath}`;
    } catch (error) {
      // Fallback to production API
      const apiBase = 'https://api.angelwatchedu.org';
      return avatarPath.startsWith('http') ? avatarPath : `${apiBase}${avatarPath}`;
    }
  };

  // Load user avatar on mount
  useEffect(() => {
    const loadUserAvatar = async () => {
      try {
        const response = await ProfileServices.getMyProfile();
        if (response?.profile?.avatarUrl) {
          setUserAvatar(response.profile.avatarUrl);
          // Update localStorage user object
          const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
          const updatedUser = { ...currentUser, avatarUrl: response.profile.avatarUrl };
          localStorage.setItem("user", JSON.stringify(updatedUser));
        }
      } catch (error) {
        console.error("Error loading user avatar:", error);
      }
    };
    loadUserAvatar();
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };

    if (showProfile) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfile]);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      logoutUser();
      // Navigate to login page after logout
      navigate('/login');
    }
    setShowProfile(false);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const response = await ProfileServices.uploadAvatar(file);
      if (response?.avatarUrl) {
        setUserAvatar(response.avatarUrl);
        // Update localStorage user object
        const updatedUser = { ...user, avatarUrl: response.avatarUrl };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        // Trigger a refresh of AuthContext if needed
        window.dispatchEvent(new Event('storage'));
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
      alert(error.response?.data?.error || 'Failed to upload avatar. Please try again.');
    } finally {
      setIsUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const isActive = (path) => {
    if (Array.isArray(path)) {
      return path.some(p => location.pathname.startsWith(p));
    }
    return location.pathname === path || location.pathname.startsWith(path);
  };

  return (
    <nav className="top-navbar">
      <div className="top-navbar-container">
        {/* Logo */}
        <Link to="/" className="top-navbar-logo">
          <img src={logo} alt="Logo" className="logo-img" />
        </Link>

        {/* Desktop Navigation */}
        <div className="top-navbar-menu">
          {menuItems.map((item, index) => {
            if (item.megaMenu) {
              return (
                <MegaMenu
                  key={index}
                  label={item.label}
                  sections={item.megaMenu.sections}
                  to={item.to}
                  hover={item.megaMenu.hover !== false}
                  className={isActive(item.activePaths || item.to) ? 'active' : ''}
                />
              );
            } else if (item.subtitle || item.blink) {
              // Renew-style nudge: an optionally-blinking label pill with a caption
              // (the subscription expiration date) underneath it.
              return (
                <Link
                  key={index}
                  to={item.to}
                  className={`top-navbar-link nav-renew ${isActive(item.activePaths || item.to) ? 'active' : ''}`}
                >
                  <span className={`nav-renew-pill ${item.blink ? 'nav-blink' : ''}`}>{item.label}</span>
                  {item.subtitle ? <span className="nav-renew-exp">{item.subtitle}</span> : null}
                </Link>
              );
            } else {
              return (
                <Link
                  key={index}
                  to={item.to}
                  className={`top-navbar-link ${isActive(item.activePaths || item.to) ? 'active' : ''}`}
                >
                  {item.label}
                </Link>
              );
            }
          })}
        </div>

        {/* Right Side Actions */}
        <div className="top-navbar-actions">
          {/* Multi-school admin: switch the school being managed */}
          <SchoolSwitcher />

          {/* Search (optional, can be added per role) */}

          {/* User Menu */}
          <div className="top-navbar-user-menu" ref={profileMenuRef}>
            <button
              className="top-navbar-user-button"
              onClick={() => setShowProfile(!showProfile)}
              aria-expanded={showProfile}
            >
              <div className="user-avatar">
                {userAvatar ? (
                  <img 
                    src={getAvatarUrl(userAvatar)} 
                    alt={user?.name || 'User'} 
                    className="user-avatar-img"
                    onError={(e) => {
                      // Fallback to logo if image fails to load
                      e.target.style.display = 'none';
                      const logoImg = e.target.parentElement.querySelector('.user-avatar-logo-fallback');
                      if (logoImg) logoImg.style.display = 'flex';
                    }}
                  />
                ) : null}
                <img 
                  src={logo} 
                  alt="Angelwatch" 
                  className={`user-avatar-logo ${userAvatar ? 'user-avatar-logo-fallback' : ''}`}
                  style={{ display: userAvatar ? 'none' : 'flex' }}
                />
              </div>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={handleAvatarChange}
            />

            {showProfile && (
              <div className="user-dropdown">
                <div className="user-dropdown-header">
                  <div 
                    className="user-avatar-large"
                    onClick={handleAvatarClick}
                    style={{ cursor: 'pointer', position: 'relative' }}
                    title="Click to upload avatar"
                  >
                    {userAvatar ? (
                      <img 
                        src={getAvatarUrl(userAvatar)} 
                        alt={user?.name || 'User'} 
                        className="user-avatar-img-large"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const logoImg = e.target.parentElement.querySelector('.user-avatar-logo-large-fallback');
                          if (logoImg) logoImg.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <img 
                      src={logo} 
                      alt="Angelwatch" 
                      className={`user-avatar-logo-large ${userAvatar ? 'user-avatar-logo-large-fallback' : ''}`}
                      style={{ display: userAvatar ? 'none' : 'flex' }}
                    />
                    {isUploadingAvatar && (
                      <div className="avatar-upload-overlay">
                        <div className="avatar-upload-spinner"></div>
                      </div>
                    )}
                  </div>
                  <div className="user-info">
                    <div className="user-name">{user?.name || 'User'}</div>
                    <div className="user-role">
                      {role === 'admin' ? 'Administrator' :
                       role === 'state' ? 'State Administrator' :
                       role === 1 ? 'School' :
                       role === 2 ? 'School Teacher' :
                       role === 3 ? 'Teacher' : 'User'}
                    </div>
                  </div>
                </div>
                <div className="user-dropdown-menu">
                  <button 
                    className="user-dropdown-item" 
                    onClick={handleAvatarClick}
                    disabled={isUploadingAvatar}
                  >
                    {isUploadingAvatar ? 'Uploading...' : '📷 Upload Avatar'}
                  </button>
                  {role === 'admin' && (
                    <>
                      <Link to="/admin/admin-dashboard" className="user-dropdown-item">
                        Dashboard
                      </Link>
                    </>
                  )}
                  <button className="user-dropdown-item" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-nav-menu">
          {menuItems.map((item, index) => (
            <div key={index} className="mobile-nav-item">
              {item.megaMenu ? (
                <div className="mobile-nav-accordion">
                  <button className="mobile-nav-accordion-header">
                    {item.label}
                  </button>
                  <div className="mobile-nav-accordion-content">
                    {item.megaMenu.sections.map((section, sIndex) => (
                      <div key={sIndex} className="mobile-nav-section">
                        {section.title && (
                          <div className="mobile-nav-section-title">{section.title}</div>
                        )}
                        {section.items.map((subItem, iIndex) => (
                          <Link
                            key={iIndex}
                            to={subItem.to}
                            className="mobile-nav-link"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {subItem.icon && <span className="mobile-nav-icon">{subItem.icon}</span>}
                            <span>{subItem.label}</span>
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (item.subtitle || item.blink) ? (
                <Link
                  to={item.to}
                  className="mobile-nav-link nav-renew-mobile"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className={`nav-renew-pill ${item.blink ? 'nav-blink' : ''}`}>{item.label}</span>
                  {item.subtitle ? <span className="nav-renew-exp">{item.subtitle}</span> : null}
                </Link>
              ) : (
                <Link
                  to={item.to}
                  className="mobile-nav-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </nav>
  );
};

TopNavbar.propTypes = {
  role: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      to: PropTypes.string,
      activePaths: PropTypes.arrayOf(PropTypes.string),
      megaMenu: PropTypes.shape({
        sections: PropTypes.array.isRequired,
        hover: PropTypes.bool,
      }),
    })
  ).isRequired,
  onLogout: PropTypes.func,
};

export default TopNavbar;

